// index.js — WAVY shared UI primitives (Wave 1 design system).
//
// One entry point for the design-system layer. Components are presentation
// only: they never call APIs or read auth state, so every later UI wave can
// adopt them without touching workflow/backend contracts.
// Preferred icon library for all new work: lucide-react.
import React from "react";
import { X } from "lucide-react";

/* ==========================================================================
   Button
   ========================================================================== */
export function Button({
  variant = "primary", // primary | secondary | ghost | danger
  size = "md", // sm | md | lg
  loading = false,
  type = "button",
  className = "",
  children,
  disabled,
  ...rest
}) {
  return (
    <button
      type={type}
      className={`wv-btn wv-btn--${variant} ${size !== "md" ? `wv-btn--${size}` : ""} ${loading ? "wv-btn--loading" : ""} ${className}`.trim()}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size={16} /> : null}
      {children}
    </button>
  );
}

/* ==========================================================================
   IconButton — accessible icon-only action (requires an accessible name).
   ========================================================================== */
export function IconButton({
  icon: Icon,
  label,
  variant = "ghost", // ghost | danger
  size = 18,
  className = "",
  ...rest
}) {
  return (
    <button
      type="button"
      className={`wv-icon-btn ${variant === "danger" ? "wv-icon-btn--danger" : ""} ${className}`.trim()}
      aria-label={label}
      title={label}
      {...rest}
    >
      <Icon size={size} aria-hidden="true" />
    </button>
  );
}

/* ==========================================================================
   Badge / StatusBadge
   ========================================================================== */
const BADGE_TONES = ["primary", "info", "success", "warning", "danger", "neutral"];

export function Badge({ tone = "neutral", className = "", children, ...rest }) {
  const safeTone = BADGE_TONES.includes(tone) ? tone : "neutral";
  return (
    <span className={`wv-badge wv-badge--${safeTone} ${className}`.trim()} {...rest}>
      {children}
    </span>
  );
}

// Canonical WAVY document/workflow status -> tone mapping. Kept in one place
// so every wave renders identical status colors.
const STATUS_TONES = {
  pending: "warning",
  "in progress": "info",
  "in-progress": "info",
  routed: "info",
  approved: "success",
  completed: "success",
  signed: "success",
  rejected: "danger",
  returned: "danger",
  overdue: "danger",
  missed: "danger",
  archived: "neutral",
  draft: "neutral",
  not_applicable: "neutral",
};

export function StatusBadge({ status, className = "" }) {
  const raw = String(status || "").trim();
  const tone = STATUS_TONES[raw.toLowerCase()] || "neutral";
  return (
    <Badge tone={tone} className={className}>
      {raw ? raw.replace(/_/g, " ") : "Unknown"}
    </Badge>
  );
}

/* ==========================================================================
   Card / Panel
   ========================================================================== */
export function Card({ title, actions, flush = false, className = "", children, ...rest }) {
  return (
    <section className={`wv-card ${className}`.trim()} {...rest}>
      {title || actions ? (
        <div className="wv-card__header">
          {title ? <h3 className="wv-card__title">{title}</h3> : null}
          {actions ? <div className="wv-card__actions">{actions}</div> : null}
        </div>
      ) : null}
      <div className={`wv-card__body ${flush ? "wv-card__body--flush" : ""}`}>{children}</div>
    </section>
  );
}

/* ==========================================================================
   PageHeader
   ========================================================================== */
export function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <header className="wv-page-header">
      <div className="wv-page-header__title-group">
        {eyebrow ? <p className="wv-page-header__eyebrow">{eyebrow}</p> : null}
        <h1 className="wv-page-header__title">{title}</h1>
        {subtitle ? <p className="wv-page-header__subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="wv-page-header__actions">{actions}</div> : null}
    </header>
  );
}

/* ==========================================================================
   FormField — label + control + hint/error wiring (ids handled for a11y).
   ========================================================================== */
let fieldSeq = 0;
export function useFieldId(prefix = "wv-field") {
  const ref = React.useRef(null);
  if (!ref.current) {
    fieldSeq += 1;
    ref.current = `${prefix}-${fieldSeq}`;
  }
  return ref.current;
}

export function FormField({ label, required, hint, error, children, id }) {
  const autoId = useFieldId();
  const fieldId = id || autoId;
  const control = React.isValidElement(children)
    ? React.cloneElement(children, {
      id: fieldId,
      "aria-invalid": error ? "true" : undefined,
      "aria-describedby": error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined,
    })
    : children;
  return (
    <div className={`wv-field ${error ? "wv-field--invalid" : ""}`.trim()}>
      {label ? (
        <label className="wv-field__label" htmlFor={fieldId}>
          {label}
          {required ? (
            <span className="wv-field__required" aria-hidden="true">
              {" *"}
            </span>
          ) : null}
        </label>
      ) : null}
      {control}
      {error ? (
        <p className="wv-field__error" id={`${fieldId}-error`} role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="wv-field__hint" id={`${fieldId}-hint`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export const Input = React.forwardRef(function Input({ className = "", ...rest }, ref) {
  return <input ref={ref} className={`wv-input ${className}`.trim()} {...rest} />;
});

export const Select = React.forwardRef(function Select({ className = "", children, ...rest }, ref) {
  return (
    <select ref={ref} className={`wv-select ${className}`.trim()} {...rest}>
      {children}
    </select>
  );
});

export const Textarea = React.forwardRef(function Textarea({ className = "", ...rest }, ref) {
  return <textarea ref={ref} className={`wv-textarea ${className}`.trim()} {...rest} />;
});

/* ==========================================================================
   Modal — focus-trapping dialog with Escape handling.
   ========================================================================== */
export function Modal({
  open,
  onClose,
  title,
  size = "md", // sm | md | lg
  footer,
  children,
}) {
  const dialogRef = React.useRef(null);
  const previouslyFocused = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    previouslyFocused.current = document.activeElement;
    const dialog = dialogRef.current;
    if (dialog) {
      const focusable = dialog.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      if (first) first.focus();
    }

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose?.();
        return;
      }
      if (event.key !== "Tab" || !dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter((el) => !el.disabled && el.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.body.style.overflow = overflow;
      previouslyFocused.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="wv-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        className={`wv-modal ${size !== "md" ? `wv-modal--${size}` : ""}`.trim()}
      >
        <div className="wv-modal__header">
          <h2 className="wv-modal__title">{title}</h2>
          <IconButton icon={X} label="Close dialog" onClick={onClose} />
        </div>
        <div className="wv-modal__body">{children}</div>
        {footer ? <div className="wv-modal__footer">{footer}</div> : null}
      </div>
    </div>
  );
}

/* ==========================================================================
   ConfirmDialog — application-standard replacement for window.confirm().
   ========================================================================== */
export function ConfirmDialog({ open, title = "Are you sure?", message, confirmLabel = "Confirm", cancelLabel = "Cancel", tone = "primary", onConfirm, onCancel, busy = false }) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button variant={tone === "danger" ? "danger" : "primary"} onClick={onConfirm} loading={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p style={{ margin: 0, color: "var(--wv-text-secondary)" }}>{message}</p>
    </Modal>
  );
}

/* ==========================================================================
   Spinner / Skeleton
   ========================================================================== */
export function Spinner({ size, label = "Loading" }) {
  return (
    <span
      className="wv-spinner wv-spinner--inline"
      style={size ? { width: size, height: size } : undefined}
      role="status"
      aria-label={label}
    />
  );
}

export function Skeleton({ variant = "text", style, className = "" }) {
  return <div className={`wv-skeleton wv-skeleton--${variant} ${className}`.trim()} style={style} aria-hidden="true" />;
}

/* ==========================================================================
   EmptyState / ErrorState
   ========================================================================== */
export function EmptyState({ title = "Nothing here yet", hint, action }) {
  return (
    <div className="wv-empty">
      <p className="wv-empty__title">{title}</p>
      {hint ? <p className="wv-empty__hint">{hint}</p> : null}
      {action}
    </div>
  );
}

export function ErrorState({ title = "Something went wrong", message, action }) {
  return (
    <div className="wv-error-state" role="alert">
      <p className="wv-error-state__title">{title}</p>
      {message ? <p className="wv-empty__hint">{message}</p> : null}
      {action}
    </div>
  );
}

/* ==========================================================================
   TableShell — horizontal-scroll wrapper + consistent table styling.
   ========================================================================== */
export function TableShell({ head, children, footer, className = "" }) {
  return (
    <div className={`wv-table-shell ${className}`.trim()}>
      <div className="wv-table-shell__scroll">
        <table>
          {head ? <thead>{head}</thead> : null}
          <tbody>{children}</tbody>
        </table>
      </div>
      {footer}
    </div>
  );
}

/* ==========================================================================
   Pagination
   ========================================================================== */
export function Pagination({ page, pageCount, onPageChange, label = "Page" }) {
  const clamped = Math.min(Math.max(1, page || 1), Math.max(1, pageCount || 1));
  return (
    <div className="wv-pagination">
      <span className="wv-pagination__info">
        {label} {clamped} of {Math.max(1, pageCount || 1)}
      </span>
      <div className="wv-pagination__controls">
        <Button variant="secondary" size="sm" disabled={clamped <= 1} onClick={() => onPageChange(clamped - 1)}>
          Previous
        </Button>
        <Button variant="secondary" size="sm" disabled={clamped >= (pageCount || 1)} onClick={() => onPageChange(clamped + 1)}>
          Next
        </Button>
      </div>
    </div>
  );
}
